import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Clock, MapPin, User, X, Loader2 } from "lucide-react";
import api from "../api";
import { useSuccessFailure } from "../context/SuccessFailureContext";
import { Button, Input, Textarea } from "./ui";

const EMPTY = { title: "", description: "", day: "", start_time: "", end_time: "", location: "", speaker: "" };

/**
 * Organizer agenda editor. Sessions are grouped by day for multi-day events.
 * Read-only display reuses the same grouping via <AgendaList/> below.
 */
const AgendaManager = ({ event, canManage = true }) => {
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!event?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/events/${event.id}/sessions/`);
      setSessions(res.data?.results || res.data || []);
    } catch (e) {
      showFailureModal("Error", "Could not load the agenda.");
    } finally {
      setLoading(false);
    }
  }, [event?.id, showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...EMPTY, day: event?.date || "" }); setEditingId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({
      title: s.title || "", description: s.description || "", day: s.day || "",
      start_time: (s.start_time || "").slice(0, 5), end_time: (s.end_time || "").slice(0, 5),
      location: s.location || "", speaker: s.speaker || "",
    });
    setEditingId(s.id); setShowForm(true);
  };

  const change = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start_time) {
      return showFailureModal("Missing info", "Title and start time are required.");
    }
    setSaving(true);
    const payload = { ...form, day: form.day || null, end_time: form.end_time || null };
    try {
      if (editingId) await api.patch(`/events/${event.id}/sessions/${editingId}/`, payload);
      else await api.post(`/events/${event.id}/sessions/`, payload);
      setShowForm(false);
      await load();
      showSuccessModal("Saved", "Agenda updated.");
    } catch (err) {
      const data = err.response?.data;
      showFailureModal("Save failed", data ? Object.values(data).flat().join(" ") : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try { await api.delete(`/events/${event.id}/sessions/${s.id}/`); await load(); }
    catch (e) { showFailureModal("Delete failed", "Could not delete this session."); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Agenda</h2>
        {canManage && (
          <Button size="sm" onClick={openCreate} leftIcon={<Plus size={16} />}>Add session</Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-gray-500 dark:text-gray-400">
          No sessions yet. Build a multi-day agenda by adding sessions.
        </div>
      ) : (
        <AgendaList sessions={sessions} canManage={canManage} onEdit={openEdit} onDelete={remove} />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">{editingId ? "Edit session" : "New session"}</h3>
              <button onClick={() => setShowForm(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <Input name="title" label="Title *" value={form.title} onChange={change} placeholder="Keynote" />
              <div className="grid grid-cols-2 gap-3">
                <Input name="day" type="date" label="Day" value={form.day} onChange={change} />
                <Input name="location" label="Room / track" value={form.location} onChange={change} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input name="start_time" type="time" label="Start *" value={form.start_time} onChange={change} />
                <Input name="end_time" type="time" label="End" value={form.end_time} onChange={change} />
              </div>
              <Input name="speaker" label="Speaker" value={form.speaker} onChange={change} placeholder="Jane Doe" />
              <Textarea name="description" label="Description" rows={2} value={form.description} onChange={change} />
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" fullWidth onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" fullWidth loading={saving}>{editingId ? "Save" : "Add"}</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export const AgendaList = ({ sessions, canManage = false, onEdit, onDelete }) => {
  // Group by day (sessions are already ordered by day, start_time)
  const groups = {};
  sessions.forEach((s) => {
    const key = s.day || "Schedule";
    (groups[key] = groups[key] || []).push(s);
  });
  const dayLabel = (d) => {
    if (d === "Schedule") return d;
    try { return new Date(d).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
    catch (e) { return d; }
  };

  return (
    <div className="space-y-6">
      {Object.entries(groups).map(([day, items]) => (
        <div key={day}>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{dayLabel(day)}</h3>
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.id} className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 w-20 flex-shrink-0 flex items-center gap-1">
                  <Clock size={14} /> {(s.start_time || "").slice(0, 5)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 dark:text-white">{s.title}</p>
                  {s.description && <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{s.description}</p>}
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-400">
                    {s.speaker && <span className="flex items-center gap-1"><User size={12} />{s.speaker}</span>}
                    {s.location && <span className="flex items-center gap-1"><MapPin size={12} />{s.location}</span>}
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => onEdit(s)} className="text-gray-400 hover:text-blue-600"><Pencil size={16} /></button>
                    <button onClick={() => onDelete(s)} className="text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AgendaManager;
